import React from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import TeamBuilderPage from '../components/Succeedo/TeamBuilder/TeamBuilderPage';
import AppLayout from '../components/Succeedo/Layout/AppLayout';

const GET_TEAM_DATA = gql`
  query GetTeamData {
    currentUser {
      id
      name
      province
      industry_sector
      succeedo_company_name
      advisory_team_members {
        id
        role
        status
        name
        email
        phone
        firm
        notes
        coach_profile_id
        introduction_request_id
      }
      deal_teams {
        id
        name
        status
      }
    }
    partners {
      id
      name
      org_name
      partner_category
      bio
      geography_regions
      industry_tags
      avatar_url
      rating
      verified
      intro_state
      intro_request_id
      on_my_team
      advisory_team_member_id
    }
    my_sponsorship
  }
`;

// Phase 5: the owner's advisor and their network.
const SPONSORSHIP_FIELDS = 'ok sponsorship errors';
const REMOVE_ADVISOR = gql`mutation SponsorshipRemoveAdvisor { sponsorship_remove_advisor { ${SPONSORSHIP_FIELDS} } }`;
const REQUEST_ADVISOR = gql`mutation SponsorshipRequestAdvisor { sponsorship_request_advisor { ${SPONSORSHIP_FIELDS} } }`;
const ADD_NETWORK_CONTACT = gql`
  mutation SponsorshipAddNetworkContact($contactId: ID!) {
    sponsorship_add_network_contact(contact_id: $contactId) { ${SPONSORSHIP_FIELDS} }
  }
`;

const ADD_ADVISORY_TEAM_MEMBER = gql`
  mutation AddAdvisoryTeamMember(
    $role: String!
    $status: String!
    $name: String
    $email: String
    $phone: String
    $firm: String
    $notes: String
    $coachProfileId: ID
  ) {
    add_advisory_team_member(
      role: $role
      status: $status
      name: $name
      email: $email
      phone: $phone
      firm: $firm
      notes: $notes
      coach_profile_id: $coachProfileId
    ) {
      advisory_team_member {
        id
        role
        status
        name
        email
        phone
        firm
        notes
        coach_profile_id
        introduction_request_id
      }
      errors
    }
  }
`;

const UPDATE_ADVISORY_TEAM_MEMBER = gql`
  mutation UpdateAdvisoryTeamMember(
    $id: ID!
    $status: String
    $name: String
    $email: String
    $firm: String
    $phone: String
    $notes: String
  ) {
    update_advisory_team_member(
      id: $id
      status: $status
      name: $name
      email: $email
      firm: $firm
      phone: $phone
      notes: $notes
    ) {
      advisory_team_member {
        id
        role
        status
        name
        email
        phone
        firm
        notes
        coach_profile_id
        introduction_request_id
      }
      errors
    }
  }
`;

const REMOVE_ADVISORY_TEAM_MEMBER = gql`
  mutation RemoveAdvisoryTeamMember($id: ID!) {
    remove_advisory_team_member(id: $id) {
      advisory_team_member {
        id
      }
      errors
    }
  }
`;

const CREATE_INTRODUCTION_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!) {
    create_introduction_request(partner_category: $partnerCategory) {
      introduction_request
      errors
    }
  }
`;

const CREATE_DEAL_TEAM = gql`
  mutation CreateDealTeam(
    $name: String!
    $description: String
    $advisoryTeamMemberIds: [ID!]
    $additionalEmailInvites: [EmailInviteInput!]
  ) {
    create_deal_team(
      name: $name
      description: $description
      advisory_team_member_ids: $advisoryTeamMemberIds
      additional_email_invites: $additionalEmailInvites
    ) {
      deal_team {
        id
        name
      }
      errors
    }
  }
`;

function SucceedoTeamWithData() {
  const { data, loading: queryLoading, refetch } = useQuery(GET_TEAM_DATA);
  const [addMember] = useMutation(ADD_ADVISORY_TEAM_MEMBER);
  const [updateMember] = useMutation(UPDATE_ADVISORY_TEAM_MEMBER);
  const [removeMember] = useMutation(REMOVE_ADVISORY_TEAM_MEMBER);
  const [createIntroductionRequest] = useMutation(CREATE_INTRODUCTION_REQUEST);
  const [createDealTeam] = useMutation(CREATE_DEAL_TEAM);
  const [removeAdvisor] = useMutation(REMOVE_ADVISOR);
  const [requestAdvisor] = useMutation(REQUEST_ADVISOR);
  const [addNetworkContact] = useMutation(ADD_NETWORK_CONTACT);

  const sponsorshipAction = async (mutate, field, variables = {}) => {
    const { data: result } = await mutate({ variables });
    const payload = result?.[field];
    if (payload?.errors?.length > 0) throw new Error(payload.errors.join(', '));
    await refetch();
    return payload;
  };

  const currentUser = data?.currentUser || null;
  const advisoryTeamMembers = currentUser?.advisory_team_members || [];
  const partners = data?.partners || [];
  // V1 product rule: one active deal team per owner. The GraphQL field
  // `deal_teams` already filters by status='active' server-side, so the
  // first row (if any) is the user's current workspace.
  const existingDealTeam = currentUser?.deal_teams?.[0] || null;

  const handleAdd = async (variables) => {
    const { data: result } = await addMember({ variables });
    const payload = result?.add_advisory_team_member;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    await refetch();
    return payload;
  };

  const handleUpdate = async (variables) => {
    const { data: result } = await updateMember({ variables });
    const payload = result?.update_advisory_team_member;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    await refetch();
    return payload;
  };

  const handleRemove = async (variables) => {
    const { data: result } = await removeMember({ variables });
    const payload = result?.remove_advisory_team_member;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    await refetch();
    return payload;
  };

  const handleRequestIntroduction = async (partnerCategory) => {
    const { data: result } = await createIntroductionRequest({
      variables: { partnerCategory },
    });
    const payload = result?.create_introduction_request;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    if (window.ahoy) {
      window.ahoy.track('succeedo_team_introduction_requested', { role: partnerCategory });
    }
    return payload;
  };

  const handleCreateDealTeam = async ({ name, advisoryTeamMemberIds, additionalEmailInvites }) => {
    const { data: result } = await createDealTeam({
      variables: {
        name,
        advisoryTeamMemberIds: advisoryTeamMemberIds || [],
        additionalEmailInvites: additionalEmailInvites || [],
      },
    });
    const payload = result?.create_deal_team;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    const dealTeamId = payload?.deal_team?.id;
    if (!dealTeamId) {
      throw new Error('Could not create deal team. Please try again.');
    }
    if (window.ahoy) {
      window.ahoy.track('succeedo_deal_team_started', { team_id: dealTeamId });
    }
    // Navigate to the new workspace. ReactOnRails apps use full-page nav
    // for cross-page transitions.
    window.location.href = `/succeedo/teams/${dealTeamId}/workspace`;
    return payload;
  };

  if (queryLoading) {
    return (
      <AppLayout breadcrumb="MY DEAL TEAM">
        <div className="flex items-center justify-center p-12">
          <div className="text-[hsl(var(--muted-foreground))] text-[16px]">Loading team...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb="MY DEAL TEAM">
      <TeamBuilderPage
        advisoryTeamMembers={advisoryTeamMembers}
        partners={partners}
        userProvince={currentUser?.province}
        userIndustry={currentUser?.industry_sector}
        ownerCompanyName={currentUser?.succeedo_company_name}
        existingDealTeam={existingDealTeam}
        onAdd={handleAdd}
        onRequestIntroduction={handleRequestIntroduction}
        onUpdate={handleUpdate}
        onRemove={handleRemove}
        onCreateDealTeam={handleCreateDealTeam}
        loading={queryLoading}
        sponsorship={data?.my_sponsorship || null}
        onRemoveAdvisor={() => sponsorshipAction(removeAdvisor, 'sponsorship_remove_advisor')}
        onRequestAdvisor={() => sponsorshipAction(requestAdvisor, 'sponsorship_request_advisor')}
        onAddNetwork={(contactId) => sponsorshipAction(addNetworkContact, 'sponsorship_add_network_contact', { contactId })}
      />
    </AppLayout>
  );
}

const SucceedoTeamContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <SucceedoTeamWithData />
  </ApolloProvider>
);

export default SucceedoTeamContainer;
